<script setup lang="ts">
/* =============================================================
   SELECT FIELD
   =============================================================
   TextField's sibling, down to the label/hint/error wiring and
   the id plumbing, so a form can mix the two without the rows
   drifting apart by a pixel.

   A native <select>, not a listbox built out of divs: it gets
   the platform's own picker on a phone, type-ahead on a desktop,
   and correct semantics everywhere, none of which is worth
   re-implementing for a country field.
   ============================================================= */

import { computed, useAttrs, useId } from 'vue'
import Icon from '@/components/Icon.vue'

defineOptions({ inheritAttrs: false })

const props = defineProps<{
  label: string
  hint?: string
  error?: string
  /** Shown as a disabled first option while nothing is chosen. */
  placeholder?: string
  modelValue?: string
}>()

const emit = defineEmits<{ 'update:modelValue': [value: string] }>()

const attrs = useAttrs()
const id = useId()
const hintId = `${id}-hint`
const errorId = `${id}-error`

const rootClass = computed(() => attrs.class as string | undefined)

const selectAttrs = computed(() => {
  const rest: Record<string, unknown> = { ...attrs }
  delete rest.class
  delete rest.style
  return rest
})

const describedBy = computed(() => {
  const ids = [props.error ? errorId : null, props.hint ? hintId : null].filter(Boolean)
  return ids.length > 0 ? ids.join(' ') : undefined
})
</script>

<template>
  <div class="field" :class="rootClass">
    <label :for="id" class="label">{{ label }}</label>

    <div class="controlWrap">
      <select
        v-bind="selectAttrs"
        :id="id"
        :value="modelValue"
        class="control"
        :class="{ controlError: error, placeholder: !modelValue }"
        :aria-invalid="error ? true : undefined"
        :aria-describedby="describedBy"
        @change="emit('update:modelValue', ($event.target as HTMLSelectElement).value)"
      >
        <option v-if="placeholder" value="" disabled>{{ placeholder }}</option>
        <slot />
      </select>
      <Icon name="chevronDown" :size="18" class="chevron" />
    </div>

    <p v-if="error" :id="errorId" class="error">
      <Icon name="alert" :size="14" />
      {{ error }}
    </p>
    <p v-else-if="hint" :id="hintId" class="hint">{{ hint }}</p>
  </div>
</template>

<style scoped>
.field {
  display: flex;
  flex-direction: column;
  gap: var(--space-3xs);
}

.label {
  font-size: var(--text-sm);
  font-weight: var(--weight-medium);
  color: var(--color-heading);
}

.controlWrap {
  position: relative;
  display: flex;
}

.control {
  width: 100%;
  height: var(--control-height);
  /* Room for the chevron, which is decorative and not clickable. */
  padding-inline: var(--control-pad-x) calc(var(--control-pad-x) + 1.25rem);
  font-family: inherit;
  font-size: var(--text-sm);
  color: var(--color-text);
  background-color: var(--color-surface);
  border: var(--border-width) solid var(--color-border-strong);
  border-radius: var(--radius-sm);
  appearance: none;
  transition:
    border-color var(--dur-fast) var(--ease),
    box-shadow var(--dur-fast) var(--ease);
}

.control:hover:not(:disabled) {
  border-color: var(--color-text-faint);
}

.control:focus {
  outline: none;
  border-color: var(--color-accent);
  box-shadow: var(--shadow-focus);
}

.control:disabled {
  background-color: var(--color-surface-sunken);
  cursor: not-allowed;
}

/* Matches TextField's ::placeholder, so an empty select and an empty input
   read as equally empty. */
.placeholder {
  color: var(--color-text-faint);
}

.controlError {
  border-color: var(--color-danger);
}

.controlError:focus {
  box-shadow: 0 0 0 3px var(--color-danger-soft);
}

.chevron {
  position: absolute;
  inset-block: 0;
  inset-inline-end: var(--space-2xs);
  margin-block: auto;
  color: var(--color-text-faint);
  pointer-events: none;
}

.hint,
.error {
  display: flex;
  align-items: center;
  gap: var(--space-3xs);
  font-size: var(--text-xs);
  line-height: var(--leading-snug);
}

.hint {
  color: var(--color-text-faint);
}

.error {
  color: var(--color-danger);
}

.error svg {
  flex: none;
}
</style>
